import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Caption, Icon, Option, TextButton, Tooltip } from '@joor/design-system'
import { isEqual } from 'lodash'
import { useSelector } from 'react-redux'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import {
  getAccountId,
  isLiteRetailer as isLiteRetailerSelector,
} from 'store/currentUser/selectors'

import { ImportOrdersModalIds } from 'modals/OrderList/OrderImportModal/orderImportModal.ids'

import { ImportOrdersContext } from '../../ImportOrders.context'
import { ImportOrdersVerticalHorizontalContext } from '../../ImportOrdersVerticalHorizontal.context'
import { useGetDropdownProps } from '../SecondStep.hooks'
import {
  useLinesheets,
  usePriceTypes,
  useShippingBilling,
} from './SecondStepVerticalHorizontal.hooks'
import {
  Container,
  DoorDropdownInfo,
  LinesheetsDropdownContainer,
  StyledDropdown,
} from './SecondStepVerticalHorizontal.styles'
import {
  OrderStatus,
  PriceTypesQuery,
  ShippingBillingQuery,
} from './SecondStepVerticalHorizontal.types'
import { getOrderStatusOptions } from './SecondStepVerticalHorizontal.utils'
import {
  getBillingAddressOptions,
  getLineSheetOptions,
  getPaymentMethodsOptions,
  getPriceTypesOptions,
  getShippingAddressesOptions,
  getShippingMethodsOptions,
} from './SecondStepVerticalHorizontal.utils'

type LinesheetFilter = 'all' | 'visible'

const LinesheetCaption: Record<LinesheetFilter, string> = {
  all: 'Listing all your linesheets',
  visible: 'Listing linesheets visible to retailer',
} as const

const LinesheetLoadButton: Record<LinesheetFilter, string> = {
  all: 'Load visible to Retailer',
  visible: 'Load all your linesheets',
} as const

const LinesheetTagIds: Record<LinesheetFilter, string> = {
  all: ImportOrdersModalIds.LoadVisibleLinesheetsLinkId,
  visible: ImportOrdersModalIds.LoadAllLinesheetsLinkId,
} as const

const SecondStepVerticalHorizontal: FC = () => {
  const {
    brand,
    brandWarehousesOptions,
    connectedAccountsOptions,
    door,
    doorsOptions,
    includeDoor,
    isLoadingConnectedAccounts,
    orderType,
    orderTypeOptions,
    retailer,
    retailerSearchText,
    retailerWarehousesOptions,
    warehouse,
    setBrand,
    setDoor,
    setNextDisabled,
    setOrderType,
    setRetailer,
    setRetailerSearchText,
    setWarehouse,
    openedDropdown,
    setOpenedDropdown,
  } = useContext(ImportOrdersContext)

  const {
    billingAddress,
    billingAddressOptions,
    linesheet,
    linesheetOptions,
    orderStatus,
    paymentMethod,
    paymentMethodOptions,
    priceType,
    priceTypeOptions,
    shippingAddress,
    shippingAddressOptions,
    shippingMethod,
    shippingMethodOptions,
    setBillingAddress,
    setBillingAddressOptions,
    setLinesheet,
    setLinesheetOptions,
    setOrderStatus,
    setPaymentMethod,
    setPaymentMethodOptions,
    setPriceType,
    setPriceTypeOptions,
    setShippingAddress,
    setShippingAddressOptions,
    setShippingMethod,
    setShippingMethodOptions,
  } = useContext(ImportOrdersVerticalHorizontalContext)

  const [linesheetFilter, setLinesheetFilter] = useState<LinesheetFilter>('all')
  const [connectedAccountSearch, setConnectedAccountSearch] =
    useState(retailerSearchText)
  const retailerSearchTimeout = useRef<NodeJS.Timeout | null>(null)
  const [isMandatoryField, setIsMandatoryField] = useState<boolean>(false)

  const accountId = useSelector(getAccountId) as string
  const isLiteRetailer = useSelector(isLiteRetailerSelector) as boolean
  const [brandId, retailerId] = useMemo(
    () =>
      isLiteRetailer
        ? [brand?.value || '', toGlobalId('Retailer', accountId) || '']
        : [toGlobalId('Brand', accountId) || '', retailer?.value || ''],
    [brand, retailer],
  )

  const orderStatusOptions = useMemo(
    () => getOrderStatusOptions(isLiteRetailer),
    [isLiteRetailer],
  )

  const getDropdownProps = useGetDropdownProps(
    openedDropdown,
    setOpenedDropdown,
  )

  useEffect(() => {
    const accountOption = {
      value: isLiteRetailer ? retailerId : brandId,
      label: '',
    } as Option

    isLiteRetailer ? setRetailer(accountOption) : setBrand(accountOption)
  }, [])

  const areCustomerFieldsDisabled =
    (isLiteRetailer && !brand) || (!isLiteRetailer && !retailer)

  const { linesheets, linesheetsQueryLoading, refetchLinesheets } =
    useLinesheets(
      (isLiteRetailer && brand) || (!isLiteRetailer && retailer) ? brandId : '',
    )

  const { priceTypes, priceTypeQueryLoading }: PriceTypesQuery = usePriceTypes(
    brandId,
    linesheet?.value,
    retailerId,
  )

  const {
    billingAddresses,
    paymentMethods,
    shippingAddresses,
    shippingMethods,
    retailerLocationOption,
    shippingBillingQueryLoading,
  }: ShippingBillingQuery = useShippingBilling(
    isLiteRetailer,
    brandId,
    retailerId,
  )

  const warehouseOptions =
    !retailer || !retailerWarehousesOptions.length
      ? brandWarehousesOptions
      : retailerWarehousesOptions

  useEffect(() => {
    if (isLiteRetailer) {
      setLinesheetFilter('visible')
    }
    // we only want to force the filter to retailer if it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesheetFilter])

  useEffect(() => {
    let mandatoryFieldsFilled = Boolean(
      orderStatus &&
      linesheet &&
      priceType &&
      ((isLiteRetailer && brand) || (!isLiteRetailer && retailer)),
    )

    if (!isLiteRetailer && orderStatus?.value === OrderStatus.NOTES) {
      mandatoryFieldsFilled = Boolean(
        mandatoryFieldsFilled &&
        shippingAddress &&
        billingAddress &&
        paymentMethod,
      )
    }

    setNextDisabled(!mandatoryFieldsFilled)
  }, [
    billingAddress,
    linesheet,
    orderStatus,
    paymentMethod,
    priceType,
    retailer,
    shippingAddress,
    setNextDisabled,
  ])

  useEffect(() => {
    if (priceTypeQueryLoading) return
    const priceTypeOpt = getPriceTypesOptions(priceTypes)
    if (!isEqual(priceTypeOptions, priceTypeOpt)) {
      setPriceTypeOptions(priceTypeOpt)
    }
    // Autoselect if there is only one option
    if (
      priceTypeOpt.length === 1 &&
      priceType?.value !== priceTypeOpt[0]?.value
    ) {
      setPriceType(priceTypeOpt[0])
    }
  }, [
    priceTypeOptions,
    priceTypeQueryLoading,
    priceType,
    priceTypes,
    setPriceType,
    setPriceTypeOptions,
  ])

  useEffect(() => {
    if (linesheetsQueryLoading) return
    const linesheetOpt = getLineSheetOptions(linesheets)
    // Autoselect if there is only one option
    if (
      linesheetOpt.length === 1 &&
      linesheet?.value !== linesheetOpt[0]?.value
    ) {
      setLinesheet(linesheetOpt[0])
    }
    if (!isEqual(linesheetOptions, linesheetOpt)) {
      setLinesheetOptions(linesheetOpt)
    }
  }, [
    linesheet,
    linesheetOptions,
    linesheets,
    linesheetsQueryLoading,
    setLinesheet,
    setLinesheetOptions,
  ])

  useEffect(() => {
    if (shippingBillingQueryLoading) return
    if (!shippingAddresses.length && !retailerLocationOption) {
      if (!shippingAddressOptions.length) return
      setShippingAddressOptions([])
    }

    if (!shippingAddresses.length && retailerLocationOption) {
      if (shippingAddress?.value !== retailerLocationOption.value) {
        setShippingAddress(retailerLocationOption)
      }
      const retailerLocationOptions = [retailerLocationOption]
      if (!isEqual(shippingAddressOptions, retailerLocationOptions)) {
        setShippingAddressOptions(retailerLocationOptions)
      }
      return
    }

    const shippingAddressesOpt = getShippingAddressesOptions(shippingAddresses)
    // Autoselect if there is only one option
    if (
      shippingAddressesOpt.length === 1 &&
      shippingAddress?.value !== shippingAddressesOpt[0]?.value
    ) {
      setShippingAddress(shippingAddressesOpt[0])
    }
    if (!isEqual(shippingAddressOptions, shippingAddressesOpt)) {
      setShippingAddressOptions(shippingAddressesOpt)
    }
  }, [
    retailerLocationOption,
    shippingAddress,
    shippingAddressOptions,
    shippingAddresses,
    shippingBillingQueryLoading,
    setShippingAddress,
    setShippingAddressOptions,
  ])

  useEffect(() => {
    if (shippingBillingQueryLoading) return
    if (!billingAddresses.length && !retailerLocationOption) {
      if (!billingAddressOptions.length) return
      setBillingAddressOptions([])
    }

    if (!billingAddresses.length && retailerLocationOption) {
      if (billingAddress?.value !== retailerLocationOption.value) {
        setBillingAddress(retailerLocationOption)
      }
      const retailerLocationOptions = [retailerLocationOption]
      if (!isEqual(billingAddressOptions, retailerLocationOptions)) {
        setBillingAddressOptions(retailerLocationOptions)
      }
      return
    }

    const billingAddressesOpt = getBillingAddressOptions(billingAddresses)
    // Autoselect if there is only one option
    if (
      billingAddressesOpt.length === 1 &&
      billingAddress?.value !== billingAddressesOpt[0]?.value
    ) {
      setBillingAddress(billingAddressesOpt[0])
    }
    if (!isEqual(billingAddressOptions, billingAddressesOpt)) {
      setBillingAddressOptions(billingAddressesOpt)
    }
  }, [
    billingAddress,
    billingAddressOptions,
    billingAddresses,
    retailerLocationOption,
    shippingBillingQueryLoading,
    setBillingAddress,
    setBillingAddressOptions,
  ])

  useEffect(() => {
    if (shippingBillingQueryLoading) return
    const formattedOptions = getPaymentMethodsOptions(paymentMethods)
    // Autoselect if there is only one option
    if (
      formattedOptions.length === 1 &&
      paymentMethod?.value !== formattedOptions[0]?.value
    ) {
      setPaymentMethod(formattedOptions[0])
    }
    if (!isEqual(paymentMethodOptions, formattedOptions)) {
      setPaymentMethodOptions(formattedOptions)
    }
  }, [
    paymentMethod,
    paymentMethodOptions,
    paymentMethods,
    shippingBillingQueryLoading,
    setPaymentMethod,
    setPaymentMethodOptions,
  ])

  useEffect(() => {
    if (shippingBillingQueryLoading) return
    const formattedOptions = getShippingMethodsOptions(shippingMethods)
    if (!isEqual(shippingMethodOptions, formattedOptions)) {
      setShippingMethodOptions(formattedOptions)
    }
  }, [
    shippingBillingQueryLoading,
    shippingMethodOptions,
    shippingMethods,
    setShippingMethodOptions,
  ])

  useEffect(() => {
    if (!isLiteRetailer) {
      setIsMandatoryField(orderStatus?.value === OrderStatus.NOTES)
    }
    setOrderStatus(orderStatusOptions[0])
  }, [orderStatus, setOrderStatus])

  const onSelectConnectedAccount = ({ selected }: { selected: Option }) => {
    if (isEqual(selected.id, isLiteRetailer ? brand?.id : retailer?.id)) return
    if (!isLiteRetailer && !!retailer && linesheetFilter === 'visible') {
      refetchLinesheets({
        brandId: isLiteRetailer ? selected.id : brandId,
        retailerId: !isLiteRetailer ? selected.id : retailerId,
      })
    }

    // Connected Account ids come as BrandAccount or RetailerAccount
    // We need the brand id encoded as Brand for the RetailerBrandDoors query
    if (isLiteRetailer) {
      const brandId = toGlobalId('Brand', fromGlobalId(selected.value).id)
      setBrand({ ...selected, value: brandId as string })
    } else {
      // but need the retailer id encoded as RetailerAccount for the SecondStepSharedData query
      setRetailer(selected)
    }

    setLinesheet(undefined)
    setPriceType(undefined)
    setShippingAddress(undefined)
    setBillingAddress(undefined)
    setPaymentMethod(undefined)
    setShippingMethod(undefined)
    setDoor(null)
    setConnectedAccountSearch('')
  }

  const onCAInputChange = (inputText: string) => {
    setConnectedAccountSearch(inputText)
    if (retailerSearchTimeout.current) {
      clearTimeout(retailerSearchTimeout.current)
    }
    retailerSearchTimeout.current = setTimeout(() => {
      if (inputText !== retailerSearchText) {
        setRetailerSearchText(inputText)
      }
    }, 500)
  }

  const onSelectLinesheet: (args: { selected: Option }) => void = ({
    selected,
  }) => {
    setLinesheet(selected)
    setPriceTypeOptions([])
    setPriceType(undefined)
  }

  const onSelectPriceType: (args: { selected: Option }) => void = ({
    selected,
  }) => setPriceType(selected)

  const onSelectShippingAddress: (args: { selected: Option }) => void = ({
    selected,
  }) => setShippingAddress(selected)

  const onSelectBillingAddress: (args: { selected: Option }) => void = ({
    selected,
  }) => setBillingAddress(selected)

  const onSelectPaymentMethod: (args: { selected: Option }) => void = ({
    selected,
  }) => setPaymentMethod(selected)

  const onSelectShippingMethod: (args: { selected: Option }) => void = ({
    selected,
  }) => setShippingMethod(selected)

  const handleLoadLinesheets = async () => {
    const { error, errors } = await refetchLinesheets({
      brandId: brandId,
      retailerId: linesheetFilter === 'all' ? retailer?.value : undefined,
    })

    if (!error && !errors?.length) {
      setLinesheetFilter(linesheetFilter !== 'all' ? 'all' : 'visible')
    }
  }

  return (
    <Container>
      {!isLiteRetailer && (
        <StyledDropdown
          id={ImportOrdersModalIds.OrderStatusDropdown}
          testId={ImportOrdersModalIds.OrderStatusDropdown}
          label="Order Status"
          placeholder="Select One"
          options={orderStatusOptions}
          value={orderStatus}
          onSelect={({ selected }: { selected: Option }) =>
            setOrderStatus(selected)
          }
          isRequired
          {...getDropdownProps('orderStatus')}
        />
      )}
      <StyledDropdown
        id={
          isLiteRetailer
            ? ImportOrdersModalIds.RetailerDropdown
            : ImportOrdersModalIds.BrandDropdown
        }
        testId={
          isLiteRetailer
            ? ImportOrdersModalIds.RetailerDropdown
            : ImportOrdersModalIds.BrandDropdown
        }
        label={isLiteRetailer ? 'Brand' : 'Retailer'}
        isLoading={isLoadingConnectedAccounts}
        isDisabled={linesheetsQueryLoading}
        options={connectedAccountsOptions}
        value={isLiteRetailer ? brand : retailer}
        translateOptions={false}
        isSearchable
        isRequired
        inputValue={connectedAccountSearch}
        onSelect={onSelectConnectedAccount}
        placeholder="Start typing to search..."
        onInputChange={onCAInputChange}
        dropdownIndicatorIconName="search"
        isDropdownIndicatorIconAnimated={false}
        {...getDropdownProps('customer')}
      />
      {!includeDoor && doorsOptions.length > 0 && (
        <StyledDropdown
          id={ImportOrdersModalIds.SearchDoorDropdownId}
          testId={ImportOrdersModalIds.SearchDoorDropdownId}
          label="Door"
          labelId={ImportOrdersModalIds.SearchDoorTitleId}
          containerId={ImportOrdersModalIds.SearchDoorDropdownId}
          placeholder="Select One"
          isClearable
          options={doorsOptions}
          value={door}
          onSelect={({ selected }: { selected: Option }) => setDoor(selected)}
          onClear={() => setDoor(null)}
          {...getDropdownProps('door')}
        />
      )}
      {!!warehouseOptions.length && (
        <StyledDropdown
          label="Warehouse"
          options={warehouseOptions}
          value={warehouse}
          translateOptions={false}
          onSelect={({ selected }: { selected: Option }) =>
            setWarehouse(selected)
          }
          placeholder="Select one"
          {...getDropdownProps('warehouse')}
        />
      )}
      <LinesheetsDropdownContainer>
        <div className="label">
          <label>
            Linesheet <span style={{ color: 'var(--color-error-500)' }}>*</span>
          </label>
          {!isLiteRetailer && (
            <TextButton
              id={LinesheetTagIds[linesheetFilter]}
              disabled={!retailer || linesheetsQueryLoading}
              onClick={handleLoadLinesheets}
            >
              {LinesheetLoadButton[linesheetFilter]}
            </TextButton>
          )}
        </div>
        <StyledDropdown
          id={ImportOrdersModalIds.LinesheetDropdown}
          testId={ImportOrdersModalIds.LinesheetDropdown}
          placeholder={linesheetsQueryLoading ? 'Loading...' : 'Select One'}
          options={linesheetOptions}
          value={linesheet || 'Select One'}
          onSelect={onSelectLinesheet}
          isRequired
          isLoading={linesheetsQueryLoading}
          isDisabled={
            linesheetsQueryLoading ||
            priceTypeQueryLoading ||
            areCustomerFieldsDisabled
          }
          {...getDropdownProps('linesheet')}
        />
        {!isLiteRetailer && (
          <Caption className="caption">
            {retailer
              ? `${LinesheetCaption[linesheetFilter]} (${linesheetOptions.length})`
              : ' '}
          </Caption>
        )}
      </LinesheetsDropdownContainer>
      <StyledDropdown
        id={ImportOrdersModalIds.PriceTypeDropdown}
        testId={ImportOrdersModalIds.PriceTypeDropdown}
        isDisabled={
          areCustomerFieldsDisabled || priceTypeQueryLoading || !linesheet
        }
        isLoading={priceTypeQueryLoading}
        label="Price Type"
        placeholder="Select One"
        options={priceTypeOptions}
        value={priceType}
        onSelect={onSelectPriceType}
        isRequired
        {...getDropdownProps('priceType')}
      />
      {!isLiteRetailer && (
        <DoorDropdownInfo>
          <StyledDropdown
            id={ImportOrdersModalIds.ShippingAddressDropdown}
            testId={ImportOrdersModalIds.ShippingAddressDropdown}
            label="Shipping Address"
            labelId={ImportOrdersModalIds.ShippingAddressTitleId}
            placeholder="Select One"
            options={shippingAddressOptions}
            value={shippingAddress}
            onSelect={onSelectShippingAddress}
            isDisabled={
              areCustomerFieldsDisabled || shippingBillingQueryLoading
            }
            isRequired={isMandatoryField}
            isLoading={shippingBillingQueryLoading}
            {...getDropdownProps('shippingAddress')}
          />
          {(includeDoor || door?.label) && (
            <Tooltip
              customContentStyles={{ maxWidth: '384px' }}
              content="If there is a Shipping Address assigned to your Door location,
                we will assign that shipping address to the created order,
                and the Shipping Address selected in this dropdown will be ignored."
            >
              <Icon
                iconName="information"
                iconSize="small"
                iconColor="primary-600"
              />
            </Tooltip>
          )}
        </DoorDropdownInfo>
      )}
      {!isLiteRetailer && (
        <StyledDropdown
          id={ImportOrdersModalIds.BillingAddressDropdown}
          testId={ImportOrdersModalIds.BillingAddressDropdown}
          label="Billing Address"
          placeholder="Select One"
          options={billingAddressOptions}
          value={billingAddress}
          onSelect={onSelectBillingAddress}
          isDisabled={areCustomerFieldsDisabled || shippingBillingQueryLoading}
          isLoading={shippingBillingQueryLoading}
          isRequired={isMandatoryField}
          {...getDropdownProps('billingAddress')}
        />
      )}
      {!isLiteRetailer && (
        <StyledDropdown
          id={ImportOrdersModalIds.PaymentMethodDropdown}
          testId={ImportOrdersModalIds.PaymentMethodDropdown}
          label="Payment Method"
          placeholder="Select One"
          options={paymentMethodOptions}
          value={paymentMethod}
          onSelect={onSelectPaymentMethod}
          isDisabled={areCustomerFieldsDisabled || shippingBillingQueryLoading}
          isLoading={shippingBillingQueryLoading}
          isRequired={isMandatoryField}
          {...getDropdownProps('paymentMethod')}
        />
      )}
      {!isLiteRetailer && (
        <StyledDropdown
          id={ImportOrdersModalIds.ShippingMethodDropdown}
          testId={ImportOrdersModalIds.ShippingMethodDropdown}
          label="Shipping Method"
          placeholder="Select One"
          options={shippingMethodOptions}
          value={shippingMethod}
          onSelect={onSelectShippingMethod}
          isDisabled={areCustomerFieldsDisabled || shippingBillingQueryLoading}
          isLoading={shippingBillingQueryLoading}
          onClear={() => setShippingMethod(undefined)}
          {...getDropdownProps('shippingMethod')}
        />
      )}
      {!!orderTypeOptions.length && (
        <StyledDropdown
          label="Order Type"
          placeholder="Select one"
          options={orderTypeOptions}
          value={orderType}
          translateOptions={false}
          onSelect={({ selected }: { selected: Option }) =>
            setOrderType(selected)
          }
          {...getDropdownProps('orderType')}
        />
      )}
    </Container>
  )
}

export default SecondStepVerticalHorizontal
